import { CREDIT_PACK, type InvoiceInfo, type PlanInfo, type ProviderBilling } from '@hemmafixa/shared';
import {
  Badge,
  Button,
  Card,
  cn,
  EmptyState,
  PageHeader,
  StatCard,
  StatusBadge,
  toast,
  toneClasses,
  type Tone,
} from '@hemmafixa/ui';
import { CalendarClock, Check, Coins, ExternalLink, FileText, Receipt } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import {
  useBuyCreditPack,
  useOpenBillingPortal,
  usePlans,
  useProviderBilling,
  useStartCheckout,
} from './api';

/** Plans, the provider's subscription and invoices (P6-06). Paying, changing plan and cancelling happen at Stripe. */
export function BillingPage() {
  const { t } = useI18n();
  const s = t.provider.subscription;
  const billing = useProviderBilling();
  const plans = usePlans();
  const portal = useOpenBillingPortal();
  useCheckoutResult();

  const subscription = billing.data?.subscription;
  return (
    <div className="grid gap-4">
      <PageHeader
        title={s.title}
        description={s.description}
        actions={
          subscription && billing.data?.enabled ? (
            <Button
              variant="outline"
              loading={portal.isPending}
              onClick={() => portal.mutate(undefined, { onError: showError(t.auth.genericError) })}
            >
              <ExternalLink aria-hidden />
              {s.manage}
            </Button>
          ) : undefined
        }
      />
      <QueryView query={billing} loading="cards">
        {(data) => (
          <>
            <SubscriptionSummary billing={data} />
            <QueryView query={plans} loading="cards">
              {(list) => <PlanCards plans={list} billing={data} />}
            </QueryView>
            <Invoices invoices={data.invoices} />
          </>
        )}
      </QueryView>
    </div>
  );
}

function showError(fallback: string) {
  return (error: unknown) => toast.error(error instanceof ApiError ? error.message : fallback);
}

/** Thanks or "cancelled" after coming back from Stripe Checkout, then removes ?checkout / ?pack from the address */
function useCheckoutResult() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const checkout = params.get('checkout');
  const pack = params.get('pack');
  useEffect(() => {
    if (!checkout && !pack) return;
    const s = t.provider.subscription;
    if (checkout === 'success') toast.success(s.checkoutSuccess);
    if (checkout === 'cancel') toast(s.checkoutCancel);
    if (pack === 'success') toast.success(s.packSuccess);
    if (pack === 'cancel') toast(s.packCancel);
    setParams(
      (current) => {
        current.delete('checkout');
        current.delete('pack');
        return current;
      },
      { replace: true },
    );
  }, [checkout, pack, setParams, t]);
}

function Notice({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <p role="status" className={cn('rounded-lg border px-4 py-3 text-sm', toneClasses[tone])}>
      {children}
    </p>
  );
}

function SubscriptionSummary({ billing }: { billing: ProviderBilling }) {
  const { t, date, language, money } = useI18n();
  const buyPack = useBuyCreditPack();
  const s = t.provider.subscription;
  const statuses = useStatuses();
  const sub = billing.subscription;

  if (!billing.enabled && !sub) return <Notice tone="info">{s.billingOff}</Notice>;
  if (!sub || sub.status === 'incomplete' || sub.status === 'canceled') {
    return <Notice tone="brand">{s.noSubscription}</Notice>;
  }

  const renewal = sub.status === 'trialing' ? sub.trialEndsAt : sub.currentPeriodEnd;
  return (
    <div className="grid gap-4">
      {sub.status === 'past_due' && sub.graceEndsAt && (
        <Notice tone="danger">{fill(s.pastDue, { date: date(sub.graceEndsAt) })}</Notice>
      )}
      {sub.cancelAtPeriodEnd && sub.currentPeriodEnd && (
        <Notice tone="warning">{fill(s.canceling, { date: date(sub.currentPeriodEnd) })}</Notice>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="grid gap-2 p-5">
          <p className="text-sm text-muted-foreground">{s.currentTitle}</p>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold">{language === 'sv' ? sub.plan.nameSv : sub.plan.nameEn}</h2>
            <StatusBadge status={sub.status} statuses={statuses.subscription} />
          </div>
          <p className="text-sm text-muted-foreground">
            {fill(s.proposalsPerMonth, { count: sub.plan.proposalsPerMonth })}
          </p>
        </Card>
        {renewal && (
          <StatCard
            label={sub.cancelAtPeriodEnd ? s.endsOn : sub.status === 'trialing' ? s.trialEnds : s.renews}
            value={date(renewal)}
            icon={CalendarClock}
          />
        )}
        {billing.credits.enforced && (
          <Card className="grid gap-3 p-5">
            <div className="flex items-center gap-3">
              <span className={cn('grid size-10 place-items-center rounded-full', toneClasses.brand)}>
                <Coins className="size-5" aria-hidden />
              </span>
              <div>
                <p className="text-sm text-muted-foreground">{s.creditsLeft}</p>
                <p className="text-2xl font-bold tabular-nums">{billing.credits.balance}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{s.creditsHint}</p>
            <Button
              variant="outline"
              loading={buyPack.isPending}
              onClick={() => buyPack.mutate(undefined, { onError: showError(t.auth.genericError) })}
            >
              {fill(s.buyPack, { credits: CREDIT_PACK.credits, price: money(CREDIT_PACK.priceOre) })}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

function PlanCards({ plans, billing }: { plans: PlanInfo[]; billing: ProviderBilling }) {
  const { t, money, language } = useI18n();
  const s = t.provider.subscription;
  const checkout = useStartCheckout();
  const sub = billing.subscription;
  const subscribed = sub && ['trialing', 'active', 'past_due'].includes(sub.status);
  // The trial is given once, so it is only offered to a provider who never subscribed
  const offerTrial = !sub;

  return (
    <section className="grid gap-3" aria-labelledby="plans-title">
      <h2 id="plans-title" className="text-lg font-bold">
        {s.plansTitle}
      </h2>
      <div className="grid gap-4 lg:grid-cols-3">
        {plans.map((plan) => {
          const current = subscribed && sub.plan.id === plan.id;
          const pending = checkout.isPending && checkout.variables === plan.id;
          return (
            <Card key={plan.id} className={cn('grid gap-4 p-5', current && 'border-2 border-primary')}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-bold">{language === 'sv' ? plan.nameSv : plan.nameEn}</h3>
                {current && <Badge tone="brand">{s.current}</Badge>}
              </div>
              {(language === 'sv' ? plan.descriptionSv : plan.descriptionEn) && (
                <p className="text-sm text-muted-foreground">
                  {language === 'sv' ? plan.descriptionSv : plan.descriptionEn}
                </p>
              )}
              <p>
                <span className="text-3xl font-bold tabular-nums">{money(plan.priceOre)}</span>{' '}
                <span className="text-sm text-muted-foreground">{s.perMonth}</span>
              </p>
              <ul className="grid gap-2 text-sm">
                {[fill(s.proposalsPerMonth, { count: plan.proposalsPerMonth }), s.allCategories].map(
                  (feature) => (
                    <li key={feature} className="flex gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                      {feature}
                    </li>
                  ),
                )}
              </ul>
              {subscribed ? (
                <Button variant="outline" disabled className="mt-auto">
                  {current ? s.current : s.changeInPortal}
                </Button>
              ) : (
                <Button
                  className="mt-auto"
                  disabled={!billing.enabled || checkout.isPending}
                  loading={pending}
                  onClick={() => checkout.mutate(plan.id, { onError: showError(t.auth.genericError) })}
                >
                  {offerTrial && plan.trialDays > 0 ? fill(s.startTrial, { days: plan.trialDays }) : s.choose}
                </Button>
              )}
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{s.vatNote}</p>
    </section>
  );
}

function Invoices({ invoices }: { invoices: InvoiceInfo[] }) {
  const { t, money, date } = useI18n();
  const s = t.provider.subscription;
  const statuses = useStatuses();

  return (
    <section className="grid gap-3" aria-labelledby="invoices-title">
      <h2 id="invoices-title" className="text-lg font-bold">
        {s.invoicesTitle}
      </h2>
      {invoices.length === 0 ? (
        <EmptyState icon={Receipt} title={s.noInvoices} />
      ) : (
        <Card className="divide-y divide-border">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-4">
              <div className="grid min-w-40 flex-1 gap-0.5">
                <span className="font-medium">{invoice.number ?? '–'}</span>
                <span className="text-sm text-muted-foreground">
                  {s.invoicePeriod}: {date(invoice.periodStart)} – {date(invoice.periodEnd)}
                </span>
              </div>
              <span className="font-semibold tabular-nums" aria-label={s.invoiceTotal}>
                {money(invoice.totalOre)}
              </span>
              <StatusBadge status={invoice.status} statuses={statuses.invoice} />
              <div className="flex gap-2">
                {invoice.hostedInvoiceUrl && (
                  <Button asChild variant="ghost" size="sm">
                    <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink aria-hidden />
                      {s.viewInvoice}
                    </a>
                  </Button>
                )}
                {invoice.invoicePdfUrl && (
                  <Button asChild variant="ghost" size="sm">
                    <a href={invoice.invoicePdfUrl} target="_blank" rel="noopener noreferrer">
                      <FileText aria-hidden />
                      {s.downloadPdf}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}
    </section>
  );
}
